import Link from "next/link";
import Image from "next/image";
import NewsletterSignup from "./NewsletterSignup";
import StoreMap from "./StoreMap";
import CookiePreferencesLink from "@/components/shared/CookiePreferencesLink";
import { BRAND } from "@/lib/assets";

type ServiceLink = { label: string; href: string; external?: boolean };

const services: ServiceLink[] = [
  { label: "Screen Printing", href: "/services/screen-printing" },
  { label: "Embroidery", href: "/services/embroidery" },
  { label: "Promotional Products", href: "/services/promotional-products" },
  { label: "Laser Engraving", href: "/services/laser-engraving" },
  { label: "Signs & Banners", href: "/services/signs-banners" },
  { label: "Vehicle Decals", href: "/services/vehicle-decals" },
  { label: "Trophies & Awards", href: "/services/trophies-awards" },
  { label: "Bronze Plaques", href: "/services/bronze-plaques" },
  { label: "Digital Services ↗", href: "https://digital.areaadvertising.com", external: true },
];

const quickLinks: ServiceLink[] = [
  { href: "/order", label: "Get a Quote" },
  { href: "/services", label: "Services" },
  { href: "/portfolio", label: "Our Work" },
  { href: "https://areaadvertising.espwebsite.com/", label: "Promo Catalog ↗", external: true },
  { href: "/about", label: "About Us" },
  { href: "/account", label: "My Account" },
];

export default function Footer() {
  return (
    <footer className="bg-black text-gray-400">
      {/* CMYK accent bar at top of footer */}
      <div className="flex h-[3px]">
        <div className="flex-1 bg-cyan-400" />
        <div className="flex-1 bg-brand-orange" />
        <div className="flex-1 bg-yellow-400" />
        <div className="flex-1 bg-black border-t border-white/10" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div>
            <Link href="/" className="inline-block mb-8">
              <Image
                src={BRAND.logo.primaryPng}
                alt="Area Advertising"
                width={96}
                height={48}
                sizes="96px"
                className="h-11 w-auto object-contain"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Your trusted partner for custom printing, signage, and promotional
              products in the tri-state area.
            </p>
            {/* Social icons */}
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/AreaAdvertising"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="text-gray-400 hover:text-white transition-colors duration-150"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-headline text-2xl text-white uppercase mb-6">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-gray-400 hover:text-white text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-headline text-2xl text-white uppercase mb-6">
              Services
            </h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.href}>
                  {service.external ? (
                    <a
                      href={service.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150"
                    >
                      {service.label}
                    </a>
                  ) : (
                    <Link
                      href={service.href}
                      className="text-gray-400 hover:text-white text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150"
                    >
                      {service.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <Link
              href="/services"
              className="inline-block mt-5 text-brand-orange hover:text-white text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150"
            >
              View All Services →
            </Link>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-headline text-2xl text-white uppercase mb-6">
              Contact
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white/5 flex items-center justify-center shrink-0 mt-1">
                  <svg
                    className="w-4 h-4 text-brand-orange"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-gray-400 mb-1">
                    Headquarters
                  </p>
                  <p className="text-white text-xs leading-relaxed">
                    824 Winchester Avenue
                    <br />
                    Ashland, KY 41101
                  </p>
                </div>
              </div>
              <p>
                <a
                  href="tel:+16063298055"
                  className="text-gray-400 hover:text-white text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150"
                >
                  (606) 329-8055
                </a>
              </p>
              <p>
                <a
                  href="mailto:online@areaadvertising.com"
                  className="text-gray-400 hover:text-white text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors duration-150"
                >
                  online@areaadvertising.com
                </a>
              </p>
              <p className="text-gray-400 text-[11px] uppercase tracking-[0.2em] font-semibold">
                Mon – Fri: 9am – 5pm EST
              </p>
            </div>

            {/* Shop location — lazy iframe, only loads when scrolled into view */}
            <StoreMap className="mt-8 aspect-video w-full border border-white/10" />

            <div className="mt-8 flex flex-col gap-3">
              <Link href="/order" className="btn-outline-white btn-sm">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter strip */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-headline text-2xl text-white uppercase mb-1">
              Stay in the Loop
            </h3>
            <p className="text-gray-400 text-xs">
              New products, seasonal promotions, and local events.
            </p>
          </div>
          <NewsletterSignup />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-[10px] uppercase tracking-[0.25em] font-semibold">
            &copy; {new Date().getFullYear()} Area Advertising. All rights reserved.
          </p>
          <div className="flex gap-6 flex-wrap">
            <Link
              href="/privacy"
              className="text-gray-400 hover:text-white text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors duration-150"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              className="text-gray-400 hover:text-white text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors duration-150"
            >
              Terms of Service
            </Link>
            <CookiePreferencesLink />
          </div>
        </div>
      </div>
    </footer>
  );
}
